/**
 * The platform's own About page, on carefull.ro and on any address nobody has registered.
 *
 * `/about` is a clinic's page: the clinic writes it and the API serves it by session or by
 * hostname. On the platform's address there is neither a session nor a clinic, so there is
 * nothing to fetch -- and the home page's closing "Learn more" button used to lead a visitor
 * straight to "The About page could not be loaded."
 *
 * So the platform ships a page of its own, the way {@link platformHomeContent} ships its front
 * page. Not configurable by anyone and deliberately so: it addresses a clinic considering
 * Carefull, where every clinic's About page addresses a patient.
 *
 * <h2>Why it is shaped like a clinic's profile</h2>
 *
 * Resolved into {@link ITenantProfile} so that `About` draws it through the very markup a
 * clinic's page goes through, rather than a second copy of it that would drift. Every string
 * is already translated into the reader's language, and it sits in the `…Ro` half with the
 * `…En` half left null: `localised` falls back to the Romanian half whenever there is no
 * English, so it hands the translated string back unchanged under either flag. The `Ro` in
 * the field name is the shape's, not a claim about the language.
 */

import { TranslateFn } from '../validationRules';
import ResourcesIds from '../../resources/ResourcesIds';
import { ITenantProfile, ITenantProfileCard } from './tenantProfile';

export const platformAboutProfile = (translate: TranslateFn): ITenantProfile => ({
    aboutTitleRo: translate(ResourcesIds.PLATFORM_ABOUT_TITLE),
    aboutSubtitleRo: translate(ResourcesIds.PLATFORM_ABOUT_SUBTITLE),
    missionTitleRo: translate(ResourcesIds.PLATFORM_ABOUT_MISSION_TITLE),
    missionBodyRo: translate(ResourcesIds.PLATFORM_ABOUT_MISSION_BODY),
    featuresTitleRo: translate(ResourcesIds.PLATFORM_ABOUT_FEATURES_TITLE),
    servicesTitleRo: translate(ResourcesIds.PLATFORM_ABOUT_SERVICES_TITLE),
    ctaTitleRo: translate(ResourcesIds.PLATFORM_ABOUT_CTA_TITLE),
    ctaSubtitleRo: translate(ResourcesIds.PLATFORM_ABOUT_CTA_SUBTITLE),
    ctaContactRo: translate(ResourcesIds.PLATFORM_ABOUT_CTA_CONTACT),
    features: [
        card(-1, 'SCHEDULE', translate(ResourcesIds.PLATFORM_ABOUT_FEATURE_BOOKING_TITLE),
            translate(ResourcesIds.PLATFORM_ABOUT_FEATURE_BOOKING_DESC)),
        card(-2, 'MONITOR_HEART', translate(ResourcesIds.PLATFORM_ABOUT_FEATURE_EVALUATIONS_TITLE),
            translate(ResourcesIds.PLATFORM_ABOUT_FEATURE_EVALUATIONS_DESC)),
        card(-3, 'FITNESS_CENTER', translate(ResourcesIds.PLATFORM_ABOUT_FEATURE_PACKAGES_TITLE),
            translate(ResourcesIds.PLATFORM_ABOUT_FEATURE_PACKAGES_DESC)),
        card(-4, 'VERIFIED', translate(ResourcesIds.PLATFORM_ABOUT_FEATURE_INVOICING_TITLE),
            translate(ResourcesIds.PLATFORM_ABOUT_FEATURE_INVOICING_DESC)),
        card(-5, 'SUPPORT_AGENT', translate(ResourcesIds.PLATFORM_ABOUT_FEATURE_REMINDERS_TITLE),
            translate(ResourcesIds.PLATFORM_ABOUT_FEATURE_REMINDERS_DESC)),
        card(-6, 'SECURITY', translate(ResourcesIds.PLATFORM_ABOUT_FEATURE_IDENTITY_TITLE),
            translate(ResourcesIds.PLATFORM_ABOUT_FEATURE_IDENTITY_DESC)),
    ],
    services: [
        card(-11, null, translate(ResourcesIds.PLATFORM_ABOUT_ROLE_ADMIN_TITLE),
            translate(ResourcesIds.PLATFORM_ABOUT_ROLE_ADMIN_DESC)),
        card(-12, null, translate(ResourcesIds.PLATFORM_ABOUT_ROLE_THERAPIST_TITLE),
            translate(ResourcesIds.PLATFORM_ABOUT_ROLE_THERAPIST_DESC)),
        card(-13, null, translate(ResourcesIds.PLATFORM_ABOUT_ROLE_PATIENT_TITLE),
            translate(ResourcesIds.PLATFORM_ABOUT_ROLE_PATIENT_DESC)),
    ],
});

/** Negative ids, so a shipped card can never share a key with one the server numbered. */
const card = (id: number, icon: string | null, title: string, body: string): ITenantProfileCard =>
    ({ id, icon, titleRo: title, bodyRo: body });
